// 261010 Epic 2 / P2b-1 — bước PROMPT của một video, CÓ thao tác (khung mới): xem bản gửi engine, sửa + lưu tay, dựng lại prompt của MỘT video (AI chữ).
// Không nhúng EventPlanTab: dùng `PromptChipEditor` (sửa chip) + `EnginePromptModal` (xem bản gửi engine) + hai API đơn lẻ. Dựng lại khi prompt đã sửa tay ⇒ hỏi và gửi `{ force: true }`.
import React, { useMemo, useState } from 'react';
import { Alert, Button, Modal, Popconfirm, Select, Space, Tag, message } from 'antd';
import { filmApi } from '../../services/api/film';
import type { EventPlan, FilmEvent } from '../../types/film';
import { useAliveRef } from '../../hooks/useAliveRef';
import PromptChipEditor from './PromptChipEditor';
import { videoRefOf } from './workflowL6Utils';
import EnginePromptModal from './EnginePromptModal';
import { actionOf, actionsFor, promptRebuildGateOf, promptCtxOf, runningKindsOf, workflowActionError, type ActionError, type WorkflowActionCtx } from './workflowActionPolicy';
import { RunningTag, StepErrorAlert, type InteractiveProps } from './WorkflowStepShared';
import { promptCueTrace } from './actingPolicy'; // 261010 P3e
import { PromptLintBlock, PromptStaleLine } from './WorkflowWarnBlocks'; // 261010 P9D D7
import PromptEvidencePanel from './PromptEvidencePanel'; // 261010 P5d — khung đọc-chỉ "Câu AI nhắc tới"; thẻ phiếu do TRANG dựng, không đi vào `actions` / ctx của nút nào. `enginePrompt` chỉ được gọi khi phiếu không có snapshot.
import { needsConfirm } from './workflowConfirmPolicy';
import { promptModeViewOf } from './promptModeView'; // U32b — kiểu prompt riêng của video (logic thuần, có test)

interface Props extends InteractiveProps {
  eventId: string;
  ev: FilmEvent;
  detail: EventPlan | null;
}

const MODE_VI: Record<string, string> = { NATURAL: 'Tự nhiên', TECHNICAL: 'Kỹ thuật' };

const WorkflowPromptStep: React.FC<Props> = ({ planId, eventId, ev, detail, stepState, jobs, onActionDone, promptPick, onClosePromptPick }) => {
  const alive = useAliveRef();
  const [err, setErr] = useState<ActionError | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewId, setViewId] = useState<string | null>(null);

  const pctx = promptCtxOf(ev);
  const modeView = promptModeViewOf(ev, detail?.plan.promptMode);
  const ctx: WorkflowActionCtx = useMemo(() => ({ isSky: true, stepState, runningJobKinds: runningKindsOf(jobs, eventId), prompt: pctx }), [stepState, jobs, eventId, pctx.hasPrompt, pctx.authorStatus, pctx.promptModeState]); // eslint-disable-line react-hooks/exhaustive-deps
  const actions = useMemo(() => actionsFor('PROMPT', ctx), [ctx]);
  const view = actionOf(actions, 'PROMPT_VIEW_ENGINE');
  const save = actionOf(actions, 'PROMPT_SAVE');
  const rebuild = actionOf(actions, 'PROMPT_REBUILD');
  const authorRunning = ctx.runningJobKinds.includes('FILM_AUTHOR_PROMPTS');

  const doSave = async (text: string) => {
    setErr(null); setSaving(true);
    try { await filmApi.saveEventPrompt(eventId, text); message.success('Đã lưu prompt'); }
    catch (e: any) { if (alive.current) setErr(workflowActionError(e, 'Lưu prompt')); }
    finally { if (alive.current) setSaving(false); onActionDone(); }
  };
  const onSave = (text: string) => {
    // Cùng luật UI cũ (L5): prompt đang lệch kiểu ⇒ Lưu là chọn snapshot mới = kiểu hiệu lực hiện tại ⇒ hỏi trước.
    if (ev.promptModeState === 'MISMATCH' && needsConfirm('PROMPT', 'REPLACE')) { // đổi kiểu của chữ đang lưu = thay thế ⇒ luôn hỏi
      Modal.confirm({
        title: `Lưu chữ này làm prompt kiểu ${MODE_VI[ev.effectivePromptMode || ''] || 'hiện hành'}?`,
        content: 'Prompt đang lưu hiện là kiểu khác — Lưu sẽ đổi sang kiểu hiệu lực của video.', okText: 'Lưu', cancelText: 'Huỷ', onOk: () => doSave(text),
      });
    } else doSave(text);
  };
  // 261010 P9D (EV28) — kiểu prompt RIÊNG của video (null = theo plan). Route đồng bộ, KHÔNG đụng chữ prompt đang lưu; lệch kiểu thì nút «Dựng lại prompt» bên dưới lo (không tự gọi AI).
  const [modeBusy, setModeBusy] = useState(false);
  const changeEventMode = async (v: 'PLAN' | 'NATURAL' | 'TECHNICAL') => {
    setErr(null); setModeBusy(true);
    try {
      await filmApi.setEventPromptMode(eventId, v === 'PLAN' ? null : v);
      message.success('Đã đổi kiểu prompt của video.');
    } catch (e: any) { if (alive.current) setErr(workflowActionError(e, 'Đổi kiểu prompt của video')); }
    finally { if (alive.current) setModeBusy(false); onActionDone(); }
  };
  const doRebuild = async () => {
    if (!rebuild) return;
    setErr(null); setRebuilding(true);
    try { await filmApi.authorOneEventPrompt(eventId, rebuild.payload || {}); message.success(pctx.hasPrompt ? 'Đã dựng lại prompt' : 'Đã dựng prompt'); }
    catch (e: any) { if (alive.current) setErr(workflowActionError(e, 'Dựng lại prompt')); }
    finally { if (alive.current) setRebuilding(false); onActionDone(); }
  };
  const confirm = promptRebuildGateOf(rebuild).confirm; // prompt EDITED ⇒ LUÔN hỏi «Thay bản bạn đã sửa tay?» (force chỉ gửi sau khi xác nhận); prompt thường chạy ngay, không hộp
  const rebuildBtn = rebuild && (
    <Button size="small" type={modeView.state === 'MISMATCH' ? 'primary' : 'default'} loading={rebuilding} disabled={!rebuild.enabled} title={rebuild.enabled ? undefined : rebuild.reason} onClick={confirm ? undefined : doRebuild} data-testid="wf-prompt-rebuild">{modeView.rebuildLabel ?? rebuild.label}</Button>
  );
  const w = (ev.promptWarnings || []).filter(Boolean);
  // 261010 UI2 (L6a): video tham chiếu đã gắn ở bước Video ⇒ có chip `@Video` để chèn vào prompt (token → @Video1 lúc gửi engine 2.5).
  const vref = videoRefOf(ev);
  const videoRefChoices = useMemo(() => (vref ? [{ kind: 'video' as const, id: vref.key, label: 'Video tham chiếu', previewUrl: null, ready: true }] : []), [vref?.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const cueTrace = promptCueTrace(ev); // 261010 P3e — chỉ trace nội bộ (không nhét hồ sơ / ngoại lệ vào prompt)

  return (
    <div data-testid="wf-prompt-step">
      <StepErrorAlert error={err} onReload={onActionDone} onClose={() => setErr(null)} />
      <PromptStaleLine ev={ev} detail={detail} />
      <Space wrap size={8} style={{ marginBottom: 8 }}>
        {ev.authorStatus && <Tag color={ev.authorStatus === 'EDITED' ? 'gold' : 'blue'}>{ev.authorStatus === 'EDITED' ? 'đã sửa tay' : 'AI dựng'}</Tag>}
        {authorRunning && <RunningTag>đang dựng prompt ở nền…</RunningTag>}
        <span className="muted" style={{ fontSize: 13 }}>Kiểu prompt:{' '}
          <Select size="small" style={{ width: 150 }} value={modeView.selectValue} loading={modeBusy} disabled={modeBusy || authorRunning} aria-label="Kiểu prompt của video" data-testid="wf-prompt-event-mode"
            options={[{ value: 'PLAN', label: 'Theo plan' }, { value: 'NATURAL', label: 'Tự nhiên' }, { value: 'TECHNICAL', label: 'Kỹ thuật' }]}
            onChange={(v) => { void changeEventMode(v as 'PLAN' | 'NATURAL' | 'TECHNICAL'); }} />
        </span>
      </Space>
      <p className={`wf-pv-modeline${modeView.state === 'MISMATCH' ? ' bad' : ''}`} data-testid="wf-prompt-mode-line">
        {modeView.line}{modeView.willOverwriteEdit ? ' Prompt này đã sửa tay — tạo lại sẽ ghi đè bản sửa.' : ''}
      </p>
      <Space wrap size={8} style={{ marginBottom: 10 }}>
        {view?.visible && <Button size="small" disabled={!view.enabled} title={view.enabled ? undefined : view.reason} onClick={() => setViewId(eventId)} data-testid="wf-prompt-view">{view.label}</Button>}
        {confirm
          ? <Popconfirm title={confirm.title} description={<div style={{ maxWidth: 300 }}>{confirm.body}</div>} okText={confirm.okText} cancelText={confirm.cancelText} disabled={!rebuild!.enabled} onConfirm={doRebuild}>{rebuildBtn}</Popconfirm>
          : rebuildBtn}
      </Space>
      {promptPick != null && <PromptEvidencePanel pick={promptPick} savedPrompt={ev.authorPrompt ?? ''} onClose={() => onClosePromptPick?.()} fetchEngineNow={() => filmApi.enginePrompt(eventId).then((d) => d.finalPrompt)} />}
      {ev.authorPrompt && save?.visible ? (
        saving || !save.enabled
          ? <pre className="wf-pre" tabIndex={0} aria-label="Prompt đang lưu">{ev.authorPrompt}</pre>
          : <PromptChipEditor value={ev.authorPrompt} bank={detail?.refBank} engine={detail?.plan.engine} extraChoices={videoRefChoices} onSave={onSave} />
      ) : <p className="muted">Chưa có prompt.{rebuild?.enabled ? ' Bấm "Dựng prompt" để AI viết.' : ''}</p>}
      <PromptLintBlock planId={planId} eventOrder={ev.eventOrder} />
      {w.length > 0 && (
        <Alert type="warning" showIcon style={{ marginTop: 10 }} message="Cảnh báo lúc dựng prompt"
          description={<ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>{w.map((x, i) => <li key={i}>{x}</li>)}</ul>} />
      )}
      {cueTrace.length > 0 && (
        <details style={{ marginTop: 10 }} data-testid="wf-prompt-cue-trace">
          <summary className="muted" style={{ cursor: 'pointer', fontSize: 12.5 }}>Cue Diễn xuất đã dùng (debug)</summary>
          <ul className="wf-list">{cueTrace.map((t) => <li key={t.order}><span className="k">#{t.order}</span>{t.text}</li>)}</ul>
        </details>
      )}
      <EnginePromptModal eventId={viewId} onClose={() => setViewId(null)} />
    </div>
  );
};

export default WorkflowPromptStep;
